import { useState } from 'react'
import { NavLink } from 'react-router-dom'
import {
  HomeIcon,
  Cog8ToothIcon,
  CubeIcon,
  ShoppingCartIcon,
  CogIcon,
  DocumentMagnifyingGlassIcon,
  ShieldCheckIcon,
  ChartBarIcon,
  BuildingStorefrontIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  DocumentTextIcon,
  TruckIcon,
} from '@heroicons/react/24/outline'
import { useAuth } from '../contexts/AuthContext'

const navigation = [
  { name: 'Dashboard', href: '/', icon: HomeIcon },
  {
    name: 'Administración',
    icon: Cog8ToothIcon,
    children: [
      { name: 'Alérgenos', href: '/admin/allergens' },
      { name: 'Unidades', href: '/admin/units' },
      { name: 'Proveedores', href: '/admin/suppliers' },
      { name: 'Ingredientes', href: '/admin/ingredients' },
      { name: 'Usuarios y Roles', href: '/admin/users' },
      { name: 'Plantillas de impresión', href: '/admin/print-templates' },
      { name: 'Impresoras', href: '/admin/printers' },
    ]
  },
  {
    name: 'Compras',
    icon: ShoppingCartIcon,
    children: [
      { name: 'Órdenes de Compra', href: '/purchases/orders' },
      { name: 'Recepción de Mercancías', href: '/purchases/receiving' },
    ]
  },
  { name: 'Inventario', href: '/inventory', icon: CubeIcon },
  { name: 'Recetas', href: '/recipes', icon: DocumentTextIcon },
  {
    name: 'Producción',
    icon: CogIcon,
    children: [
      { name: 'Órdenes de Producción', href: '/production/orders' },
    ]
  },
  { name: 'Expedición', href: '/shipments', icon: TruckIcon },
  { name: 'Trazabilidad', href: '/traceability', icon: DocumentMagnifyingGlassIcon },
  {
    name: 'Controles APPCC',
    icon: ShieldCheckIcon,
    children: [
      { name: 'Dashboard de Alertas', href: '/haccp' },
      { name: 'Zonas', href: '/haccp/zones' },
      { name: 'Equipos', href: '/haccp/equipment' },
      { name: 'Registro de Temperaturas', href: '/haccp/temperature-records' },
      { name: 'Limpieza y Desinfección', href: '/haccp/cleaning' },
      { name: 'Mantenimiento', href: '/haccp/maintenance' },
    ]
  },
  { name: 'Reportes', href: '/reports', icon: ChartBarIcon },
]

interface SidebarProps {
  open?: boolean
  onClose?: () => void
}

export default function Sidebar({ open = false, onClose }: SidebarProps) {
  const { user } = useAuth()
  const [openSections, setOpenSections] = useState<{ [key: string]: boolean }>({})

  const toggleSection = (sectionName: string) => {
    setOpenSections(prev => ({
      ...prev,
      [sectionName]: !prev[sectionName]
    }))
  }

  // Contenido del menú (reutilizado en escritorio y en el panel móvil).
  // onNavigate se invoca al pulsar un enlace (para cerrar el panel en móvil).
  const panel = (onNavigate?: () => void) => (
    <div className="flex grow flex-col gap-y-5 overflow-y-auto bg-white border-r border-gray-200 px-6">
      <div className="flex h-16 shrink-0 items-center">
        <BuildingStorefrontIcon className="h-8 w-8 text-primary-600" />
        <span className="ml-2 text-xl font-bold text-gray-900">
          APPCC Pavlova
        </span>
      </div>
      <nav className="flex flex-1 flex-col">
        <ul role="list" className="flex flex-1 flex-col gap-y-7">
          <li>
            <ul role="list" className="-mx-2 space-y-1">
              {navigation.map((item) => (
                <li key={item.name}>
                  {item.children ? (
                    <div>
                      <button
                        onClick={() => toggleSection(item.name)}
                        className="group flex w-full items-center justify-between gap-x-3 rounded-md p-2 text-sm leading-6 font-semibold text-gray-700 hover:text-primary-700 hover:bg-gray-50 transition-colors"
                      >
                        <div className="flex items-center gap-x-3">
                          <item.icon className="h-6 w-6 shrink-0" aria-hidden="true" />
                          {item.name}
                        </div>
                        {openSections[item.name] ? (
                          <ChevronDownIcon className="h-4 w-4 transition-transform" />
                        ) : (
                          <ChevronRightIcon className="h-4 w-4 transition-transform" />
                        )}
                      </button>
                      {openSections[item.name] && (
                        <ul role="list" className="mt-2 space-y-1 pl-6">
                          {item.children.map((child) => (
                            <NavLink
                              key={child.name}
                              to={child.href}
                              onClick={onNavigate}
                              className={({ isActive }) =>
                                `group flex gap-x-3 rounded-md p-2 text-sm leading-6 font-medium transition-colors ${
                                  isActive
                                    ? 'bg-primary-50 text-primary-700'
                                    : 'text-gray-600 hover:text-primary-700 hover:bg-gray-50'
                                }`
                              }
                            >
                              {child.name}
                            </NavLink>
                          ))}
                        </ul>
                      )}
                    </div>
                  ) : (
                    <NavLink
                      to={item.href}
                      onClick={onNavigate}
                      className={({ isActive }) =>
                        `group flex gap-x-3 rounded-md p-2 text-sm leading-6 font-semibold transition-colors ${
                          isActive
                            ? 'bg-primary-50 text-primary-700'
                            : 'text-gray-700 hover:text-primary-700 hover:bg-gray-50'
                        }`
                      }
                    >
                      <item.icon className="h-6 w-6 shrink-0" aria-hidden="true" />
                      {item.name}
                    </NavLink>
                  )}
                </li>
              ))}
            </ul>
          </li>
          <li className="mt-auto">
            <div className="flex items-center gap-x-4 px-2 py-3 text-sm leading-6 text-gray-900">
              <div className="h-8 w-8 rounded-full bg-primary-600 flex items-center justify-center">
                <span className="text-white font-medium">
                  {user?.name.charAt(0).toUpperCase()}
                </span>
              </div>
              <div>
                <div className="font-semibold">{user?.name}</div>
                <div className="text-gray-600 text-xs">{user?.position}</div>
              </div>
            </div>
          </li>
        </ul>
      </nav>
    </div>
  )

  return (
    <>
      {/* Escritorio: fijo, siempre visible */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:z-50 lg:flex lg:w-64 lg:flex-col">
        {panel()}
      </div>

      {/* Móvil: panel deslizante con overlay */}
      <div className={`lg:hidden ${open ? '' : 'pointer-events-none'}`}>
        <div
          className={`fixed inset-0 z-50 bg-gray-900/50 transition-opacity duration-300 ${open ? 'opacity-100' : 'opacity-0'}`}
          onClick={onClose}
          aria-hidden="true"
        />
        <div
          className={`fixed inset-y-0 left-0 z-50 flex w-64 flex-col transform transition-transform duration-300 ${open ? 'translate-x-0' : '-translate-x-full'}`}
        >
          {panel(onClose)}
        </div>
      </div>
    </>
  )
}